import React, { useEffect, useState, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { get } from 'lodash';

import {
  CheckHasReviewChanges,
  CheckFieldHasCurrentReviewChanges,
  GetFormStateName,
  NormalizeInputName,
  GetInitialValue
} from 'common/Forms/Helpers/FormChangeHelper';
import usePrevious from 'hooks/usePrevious';

const FieldChangeWrapper = (props) => {
  const { input, iValue, className, fullFormName, componentType, wrappedComponent, maxLength, placeholder, disabled, style, onClick, type, checked } = props;
  const valueChanges = useSelector(state => get(state.form[`${ fullFormName }-changes`], `values.${ NormalizeInputName(input.name) }`));
  const initialFormValues = useSelector(state => state.form[`${ fullFormName }`]?.initial);
  // const hasChangeTracking = initialFormValues.changes != null;
  const [initialValue, setInitialValue] = useState(GetInitialValue(input.name, initialFormValues, iValue));
  const [hasPreviousReviewChanges, setHasPreviousReviewChanges] = useState(null);
  const [hasCurrentReviewChanges, setHasCurrentReviewChanges] = useState(null);
  const childRef = useRef(null);
  const previousValue = usePrevious(input.value);

  useEffect(() => {
    const previousReviewChangesResult = CheckHasReviewChanges(valueChanges, iValue, initialFormValues);
    setHasPreviousReviewChanges(previousReviewChangesResult);

    // For input checkboxes we need to manipulate the parent's CSS for visual indication
    if (componentType === 'checkbox' && previousReviewChangesResult) {
      const parent = childRef.current.parentElement;

      if (previousReviewChangesResult) {
        parent.classList.add('updated-in-review');
      } else {
        parent.classList.remove('updated-in-review');
      }
    }
  }, [valueChanges]);

  useEffect(() => {
    if (!hasPreviousReviewChanges) {
      const currentReviewChangesResult = CheckFieldHasCurrentReviewChanges(input, initialValue, initialFormValues, iValue, previousValue);

      // For input checkboxes we need to adjust the values and manipulate the parent's CSS for visual indication of the change
      if (componentType === 'checkbox' && !currentReviewChangesResult.ignoreChanges) {
        let adjustedInitialFieldValue = currentReviewChangesResult.initialFieldValue;
        let adjustedInputValue = input.value;

        // Some values are normalised to array values of ['1'] for true and [] for false
        if (Array.isArray(input.value) || Array.isArray(currentReviewChangesResult.initialFieldValue)) {
          if (currentReviewChangesResult.initialFieldValue?.length === 0) {
            adjustedInitialFieldValue = false;
          } else if (currentReviewChangesResult.initialFieldValue?.length === 1 && currentReviewChangesResult.initialFieldValue[0] === '1') {
            adjustedInitialFieldValue = true;
          }

          if (input.value?.length === 1 && input.value[0] === '1') {
            adjustedInputValue = true;
          } else {
            adjustedInputValue = false;
          }
        } else {
          if (currentReviewChangesResult.initialFieldValue === null || currentReviewChangesResult.initialFieldValue === '') {
            adjustedInitialFieldValue = false;
          }

          if (input.value === '') {
            adjustedInputValue = false;
          }
        }

        const adjustedHasCurrentReviewChanges = adjustedInitialFieldValue !== adjustedInputValue;
        const parent = childRef.current.parentElement;

        setInitialValue(adjustedInitialFieldValue);
        setHasCurrentReviewChanges(adjustedHasCurrentReviewChanges);

        if (adjustedHasCurrentReviewChanges) {
          parent.classList.add('updated-in-review');
        } else {
          parent.classList.remove('updated-in-review');
        }
      } else {
        setInitialValue(currentReviewChangesResult.initialFieldValue);
        setHasCurrentReviewChanges(currentReviewChangesResult.hasCurrentReviewChanges);
      }
    }
  }, [input.value]);

  return (
    <React.Fragment>
      { wrappedComponent && React.createElement(wrappedComponent, {
        ...props,
        wrappedComponent: undefined,
        className: `${ className }${ hasPreviousReviewChanges || hasCurrentReviewChanges ? ' updated-in-review' : '' }`
      }) }
      { !wrappedComponent && componentType === 'input' && <input
        { ...input }
        disabled={ disabled }
        style={ style }
        onClick={ onClick }
        placeholder={ placeholder }
        maxLength={ maxLength }
        type={ type }
        className={ hasPreviousReviewChanges || hasCurrentReviewChanges ? `${ className } updated-in-review` : `${ className }` }
      />}
      { !wrappedComponent && componentType === 'textarea' && <textarea
        { ...input }
        disabled={ disabled }
        style={ style }
        onClick={ onClick }
        placeholder={ placeholder }
        maxLength={ maxLength }
        type={ type }
        className={ hasPreviousReviewChanges || hasCurrentReviewChanges ? `${ className } updated-in-review` : `${ className }` }
      /> }
      { !wrappedComponent && componentType === 'checkbox' && <input
        { ...input }
        disabled={ disabled }
        style={ style }
        onClick={ onClick }
        ref={ childRef }
        type={ type }
        { ...(checked != null ? { checked } : {}) }
        className={ hasPreviousReviewChanges || hasCurrentReviewChanges ? `${ className } updated-in-review` : `${ className }` }
      /> }
    </React.Fragment>
  );
};

FieldChangeWrapper.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
  }).isRequired,
  iValue: PropTypes.string,
  className: PropTypes.string,
  fullFormName: PropTypes.string,
  componentType: PropTypes.string,
  wrappedComponent: PropTypes.elementType,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  style: PropTypes.shape({
    opacity: PropTypes.any
  }),
  onClick: PropTypes.func,
  type: PropTypes.string,
  checked: PropTypes.bool
};

FieldChangeWrapper.defaultProps = {
  iValue: null,
  componentType: 'input',
  wrappedComponent: null,
  className: '',
  fullFormName: '',
  maxLength: null,
  placeholder: '',
  disabled: false,
  style: null,
  onClick: null,
  type: '',
  checked: null
};

function mapStateToProps(state) {
  const formStateName = GetFormStateName(state.form);

  return {
    fullFormName: formStateName
  };
}

export default connect(
  mapStateToProps
)(FieldChangeWrapper);
